import {
  SESSION_KINDS,
  SESSION_KIND_LABELS,
  SUGGESTED_DAILY_TASKS,
  addDays,
  DIFFICULTY_LABELS,
  formatMinutes,
  type SessionKind,
} from '@algo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { StudyTimer } from '../components/StudyTimer';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  ProgressBar,
  Select,
  Spinner,
} from '../components/ui';
import { INVALIDATE_ON_WRITE, api, qk } from '../lib/api';

export function DailyPage() {
  const queryClient = useQueryClient();
  const [date, setDate] = useState<string | undefined>(undefined);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskTopic, setTaskTopic] = useState('');
  const [taskEstimate, setTaskEstimate] = useState('30');
  const [sessionMinutes, setSessionMinutes] = useState('45');
  const [sessionKind, setSessionKind] = useState<SessionKind>('SOLVING');
  const [sessionTopic, setSessionTopic] = useState('');

  const dailyQuery = useQuery({ queryKey: qk.daily(date), queryFn: () => api.getDaily(date) });
  const topicsQuery = useQuery({ queryKey: qk.topics, queryFn: () => api.listTopics() });

  const invalidate = () =>
    Promise.all(
      [...INVALIDATE_ON_WRITE, qk.daily(date)].map((key) =>
        queryClient.invalidateQueries({ queryKey: key as readonly unknown[] }),
      ),
    );

  const day = dailyQuery.data?.day;

  const addTask = useMutation({
    mutationFn: () =>
      api.createTask({
        date: day ?? new Date().toISOString().slice(0, 10),
        title: taskTitle.trim(),
        topicId: taskTopic || null,
        estimatedMinutes: Number(taskEstimate) || null,
        done: false,
      }),
    onSuccess: async () => {
      setTaskTitle('');
      await invalidate();
    },
  });

  const toggleTask = useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) => api.updateTask(id, { done }),
    onSuccess: invalidate,
  });

  const deleteTask = useMutation({
    mutationFn: (id: string) => api.deleteTask(id),
    onSuccess: invalidate,
  });

  const addSession = useMutation({
    mutationFn: () =>
      api.createSession({
        minutes: Number(sessionMinutes) || 0,
        kind: sessionKind,
        topicId: sessionTopic || null,
        // Backdate to the viewed day when it is not today.
        ...(day && day !== new Date().toISOString().slice(0, 10)
          ? { date: `${day}T12:00:00.000Z` }
          : {}),
      }),
    onSuccess: invalidate,
  });

  const deleteSession = useMutation({
    mutationFn: (id: string) => api.deleteSession(id),
    onSuccess: invalidate,
  });

  if (dailyQuery.isLoading) return <Spinner label="Өнөөдрийн мэдээллийг ачаалж байна…" />;
  if (dailyQuery.error) {
    return <ErrorState error={dailyQuery.error} onRetry={() => void dailyQuery.refetch()} />;
  }
  if (!dailyQuery.data) return null;

  const data = dailyQuery.data;
  const topics = topicsQuery.data ?? [];
  const topicName = (id: string | null) =>
    id ? (topics.find((topic) => topic.id === id)?.name ?? null) : null;

  const doneCount = data.tasks.filter((task) => task.done).length;
  const minutesRatio = data.goalMinutes > 0 ? data.totalMinutes / data.goalMinutes : 0;
  const problemsRatio =
    data.goalProblems > 0 ? data.problemsSolved.length / data.goalProblems : 0;

  return (
    <>
      <PageHeader
        title="Өдрийн бэлтгэл"
        description={data.day}
        action={
          <div className="flex items-center gap-1">
            <Button onClick={() => setDate(addDays(data.day, -1))}>‹ Өмнөх</Button>
            <Button onClick={() => setDate(undefined)}>Өнөөдөр</Button>
            <Button
              onClick={() => setDate(addDays(data.day, 1))}
              disabled={data.day >= new Date().toISOString().slice(0, 10)}
            >
              Дараах ›
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <Card title="Хийх зүйлс" subtitle={`${doneCount}/${data.tasks.length} дууссан`}>
            <ProgressBar
              value={data.tasks.length === 0 ? 0 : doneCount / data.tasks.length}
              className="mb-4"
            />

            <ul className="space-y-1.5">
              {data.tasks.map((task) => (
                <li
                  key={task.id}
                  className="flex items-center gap-3 rounded-lg border border-edge bg-raised px-3 py-2"
                >
                  <input
                    type="checkbox"
                    checked={task.done}
                    onChange={() => toggleTask.mutate({ id: task.id, done: !task.done })}
                    className="h-4 w-4 accent-[rgb(var(--accent))]"
                  />
                  <div className="min-w-0 flex-1">
                    <p className={`truncate text-sm ${task.done ? 'text-muted line-through' : ''}`}>
                      {task.title}
                    </p>
                    <p className="text-xs text-muted">
                      {[
                        topicName(task.topicId),
                        task.estimatedMinutes ? `төлөв. ${task.estimatedMinutes}м` : null,
                        task.actualMinutes ? `бодит ${task.actualMinutes}м` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Сэдэвгүй'}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => deleteTask.mutate(task.id)}
                    aria-label="Даалгавар устгах"
                  >
                    ✕
                  </Button>
                </li>
              ))}
              {data.tasks.length === 0 && (
                <li>
                  <EmptyState
                    title="Төлөвлөсөн даалгавар алга"
                    description="Доор нэмэх эсвэл саналаас сонгоно уу."
                  />
                </li>
              )}
            </ul>

            <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
              <Input
                placeholder="Даалгавар нэмэх…"
                value={taskTitle}
                onChange={(event) => setTaskTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && taskTitle.trim()) addTask.mutate();
                }}
              />
              <Select value={taskTopic} onChange={(event) => setTaskTopic(event.target.value)}>
                <option value="">Сэдэвгүй</option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.name}
                  </option>
                ))}
              </Select>
              <Input
                type="number"
                min={0}
                className="w-24"
                value={taskEstimate}
                onChange={(event) => setTaskEstimate(event.target.value)}
                aria-label="Төлөвлөсөн минут"
              />
              <Button
                variant="primary"
                disabled={!taskTitle.trim() || addTask.isPending}
                onClick={() => addTask.mutate()}
              >
                Нэмэх
              </Button>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {SUGGESTED_DAILY_TASKS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="chip hover:border-accent hover:text-accent"
                  onClick={() => setTaskTitle(suggestion)}
                >
                  + {suggestion}
                </button>
              ))}
            </div>
          </Card>

          <Card title="Өнөөдөр бодсон бодлого">
            {data.problemsSolved.length === 0 ? (
              <EmptyState title="Хараахан бүртгээгүй" description="Бодлогууд хуудсанд бодолтоо бүртгэнэ үү." />
            ) : (
              <ul className="space-y-2">
                {data.problemsSolved.map((problem) => (
                  <li
                    key={problem.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-edge bg-raised px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm">{problem.name}</p>
                      <p className="text-xs text-muted">
                        {problem.platform} · {problem.topics.map((t) => t.name).join(', ')}
                      </p>
                    </div>
                    <Badge>{DIFFICULTY_LABELS[problem.normalizedDifficulty]}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <StudyTimer topics={topics} day={data.day} />

          <Card title="Зорилт">
            <div className="space-y-4">
              <div>
                <div className="mb-1.5 flex justify-between text-sm">
                  <span className="text-muted">Минут</span>
                  <span className="font-mono">
                    {data.totalMinutes} / {data.goalMinutes}
                  </span>
                </div>
                <ProgressBar value={minutesRatio} />
              </div>
              <div>
                <div className="mb-1.5 flex justify-between text-sm">
                  <span className="text-muted">Бодлого</span>
                  <span className="font-mono">
                    {data.problemsSolved.length} / {data.goalProblems}
                  </span>
                </div>
                <ProgressBar value={problemsRatio} />
              </div>
            </div>
          </Card>

          <Card title="Цагийн бүртгэл" subtitle={formatMinutes(data.totalMinutes)}>
            <ul className="space-y-1.5">
              {data.sessions.map((session) => (
                <li
                  key={session.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-edge bg-raised px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <p className="truncate">
                      {SESSION_KIND_LABELS[session.kind as SessionKind]} ·{' '}
                      {formatMinutes(session.minutes)}
                    </p>
                    <p className="text-xs text-muted">{topicName(session.topicId) ?? 'Сэдэвгүй'}</p>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => deleteSession.mutate(session.id)}
                    aria-label="Сесс устгах"
                  >
                    ✕
                  </Button>
                </li>
              ))}
              {data.sessions.length === 0 && (
                <li className="text-sm text-muted">Бүртгэсэн сесс алга.</li>
              )}
            </ul>

            <div className="mt-4 space-y-2">
              <p className="text-xs text-muted">
                Секундомер ашиглаагүй бол гараар нэмнэ үү.
              </p>
              <Field label="Минут">
                <Input
                  type="number"
                  min={1}
                  value={sessionMinutes}
                  onChange={(event) => setSessionMinutes(event.target.value)}
                />
              </Field>
              <Field label="Төрөл">
                <Select
                  value={sessionKind}
                  onChange={(event) => setSessionKind(event.target.value as SessionKind)}
                >
                  {SESSION_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {SESSION_KIND_LABELS[kind]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Сэдэв">
                <Select
                  value={sessionTopic}
                  onChange={(event) => setSessionTopic(event.target.value)}
                >
                  <option value="">Сэдэвгүй</option>
                  {topics.map((topic) => (
                    <option key={topic.id} value={topic.id}>
                      {topic.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button
                variant="primary"
                className="w-full"
                disabled={addSession.isPending}
                onClick={() => addSession.mutate()}
              >
                Сесс бүртгэх
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
